import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Mail, Copy, Check, X, Crown, Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TextInput, FieldLabel, PrimaryButton, SecondaryButton, Overline } from "@/components/ui-kit";
import { toast } from "sonner";

/**
 * CollaboratorsModal — invite a partner by email, list current collaborators,
 * revoke access. Owner-only actions are hidden for non-owners (they see a
 * read-only members list).
 */
export default function CollaboratorsModal({ projectId, projectName, onClose, lang }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // {owner, viewer_is_owner, collaborators:[]}
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [revoking, setRevoking] = useState("");
  const [resending, setResending] = useState("");
  const [copiedEmail, setCopiedEmail] = useState("");

  // ESC closes modal
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/collaborators`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [projectId]);

  async function invite(e) {
    e?.preventDefault?.();
    const clean = (email || "").trim().toLowerCase();
    if (!clean || !/.+@.+\..+/.test(clean)) {
      toast.error(lang === "es" ? "Email no válido" : "Invalid email");
      return;
    }
    setInviting(true);
    try {
      const res = await api.post(`/projects/${projectId}/collaborators`, { email: clean, lang });
      if (res.data.already_invited) {
        toast.info(lang === "es" ? "Esta persona ya estaba invitada" : "This person was already invited");
      } else {
        const emailSent = res.data.email_status === "sent";
        const statusMsg = res.data.collaborator.status === "active"
          ? (lang === "es" ? "Colaborador añadido" : "Collaborator added")
          : (lang === "es" ? "Invitación enviada (pendiente de registro)" : "Invite sent (pending registration)");
        if (emailSent) {
          toast.success(
            lang === "es"
              ? `${statusMsg} · Email enviado a ${clean}`
              : `${statusMsg} · Email sent to ${clean}`
          );
        } else if (res.data.email_status === "failed") {
          toast.warning(
            lang === "es"
              ? `${statusMsg}, pero no se pudo enviar el email. Copia el link manualmente.`
              : `${statusMsg}, but the email could not be sent. Copy the link manually.`
          );
        } else {
          toast.success(statusMsg);
        }
      }
      setEmail("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setInviting(false);
    }
  }

  async function revoke(targetEmail) {
    if (!window.confirm(lang === "es" ? `¿Revocar el acceso de ${targetEmail}?` : `Revoke access for ${targetEmail}?`)) return;
    setRevoking(targetEmail);
    try {
      await api.delete(`/projects/${projectId}/collaborators/${encodeURIComponent(targetEmail)}`);
      toast.success(lang === "es" ? "Acceso revocado" : "Access revoked");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRevoking("");
    }
  }

  function copyInviteLink(targetEmail) {
    const url = `${window.location.origin}/register?invite_email=${encodeURIComponent(targetEmail)}`;
    navigator.clipboard.writeText(url);
    setCopiedEmail(targetEmail);
    setTimeout(() => setCopiedEmail(""), 2200);
    toast.success(lang === "es" ? "Link de invitación copiado" : "Invite link copied");
  }

  async function resendInvite(targetEmail) {
    setResending(targetEmail);
    try {
      const res = await api.post(`/projects/${projectId}/collaborators/${encodeURIComponent(targetEmail)}/resend`);
      if (res.data.email_status === "sent") {
        toast.success(lang === "es" ? `Email reenviado a ${targetEmail}` : `Email re-sent to ${targetEmail}`);
      } else {
        toast.error(
          lang === "es"
            ? "No se pudo reenviar el email. Comparte el link manualmente."
            : "Could not re-send the email. Share the link manually."
        );
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setResending("");
    }
  }

  const viewerIsOwner = data?.viewer_is_owner;
  const collaborators = data?.collaborators || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="collaborators-modal"
    >
      <div
        className="bg-white border border-[#E8E6E1] rounded-sm max-w-2xl w-full p-7 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <Overline>{lang === "es" ? "Colaboración" : "Collaboration"}</Overline>
            <h3
              className="mt-2 text-2xl text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
            >
              {lang === "es" ? "Colaboradores del proyecto" : "Project collaborators"}
            </h3>
            <p className="text-sm text-[#5C5B5A] mt-2 max-w-md">
              {viewerIsOwner
                ? (lang === "es"
                    ? "Invita a un socio por email. Verá y podrá editar todos los módulos del plan. Solo tú podrás eliminarlo, duplicarlo o invitar a más personas."
                    : "Invite a partner by email. They'll be able to read and edit every module. Only you can delete, duplicate, or invite more people.")
                : (lang === "es"
                    ? "Estás colaborando como editor en este proyecto. Puedes ver y editar todos los módulos."
                    : "You're collaborating as an editor on this project. You can view and edit every module.")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#8A8886] hover:text-[#1A1A1A] shrink-0"
            aria-label="close"
            data-testid="collaborators-close-btn"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Owner card */}
        {data?.owner && (
          <div className="mb-5 bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-4 flex items-center gap-3">
            <div className="bg-[#B89154]/15 border border-[#B89154]/30 rounded-full p-2">
              <Crown className="h-4 w-4 text-[#B89154]" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-[#8A8886]">
                {lang === "es" ? "Propietario" : "Owner"}
              </div>
              <div className="text-sm text-[#1A1A1A] truncate">
                {data.owner.name || data.owner.email}
                {data.owner.name && (
                  <span className="text-[#8A8886] ml-2" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {data.owner.email}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invite form (owner only) */}
        {viewerIsOwner && (
          <form onSubmit={invite} className="mb-6 border border-[#E8E6E1] rounded-sm p-5 bg-white">
            <FieldLabel>{lang === "es" ? "Invitar nuevo colaborador" : "Invite new collaborator"}</FieldLabel>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8886]" strokeWidth={1.5} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={lang === "es" ? "socio@empresa.com" : "partner@company.com"}
                  className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base pl-9 pr-4 py-2.5 outline-none transition-all"
                  data-testid="invite-email-input"
                  required
                />
              </div>
              <PrimaryButton type="submit" disabled={inviting} testId="invite-submit-btn">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={2} />}
                {inviting
                  ? (lang === "es" ? "Invitando…" : "Inviting…")
                  : (lang === "es" ? "Invitar" : "Invite")}
              </PrimaryButton>
            </div>
            <p className="text-xs text-[#8A8886] mt-3">
              {lang === "es"
                ? "Si tu socio ya tiene cuenta en Atelier Business, recibirá acceso al instante. Si no, su invitación quedará pendiente y se activará en cuanto se registre con ese email."
                : "If your partner already has an Atelier Business account, they'll get instant access. Otherwise, the invite stays pending until they register with that email."}
            </p>
          </form>
        )}

        {/* Members list */}
        <div>
          <Overline>
            {lang === "es" ? `Colaboradores (${collaborators.length})` : `Collaborators (${collaborators.length})`}
          </Overline>

          {loading ? (
            <div className="mt-4 text-sm text-[#5C5B5A]">{lang === "es" ? "Cargando…" : "Loading…"}</div>
          ) : collaborators.length === 0 ? (
            <div className="mt-4 border border-dashed border-[#E8E6E1] rounded-sm p-6 text-center text-sm text-[#5C5B5A]" data-testid="collaborators-empty">
              {viewerIsOwner
                ? (lang === "es" ? "Aún no has invitado a nadie." : "You haven't invited anyone yet.")
                : (lang === "es" ? "Sin otros colaboradores." : "No other collaborators.")}
            </div>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="collaborators-list">
              {collaborators.map((c) => (
                <li
                  key={c.email}
                  className="border border-[#E8E6E1] rounded-sm p-4 flex items-start gap-3"
                  data-testid={`collab-row-${c.email}`}
                >
                  <div className="bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-full p-2 mt-0.5">
                    <Users className="h-4 w-4 text-[#2563EB]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm text-[#1A1A1A] truncate">
                        {c.name || c.email}
                      </div>
                      <span
                        className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border ${
                          c.status === "active"
                            ? "border-[#2563EB]/30 bg-[#2563EB]/5 text-[#2563EB]"
                            : "border-[#B89154]/40 bg-[#B89154]/10 text-[#8a6a37]"
                        }`}
                      >
                        {c.status === "active"
                          ? (lang === "es" ? "Activo · Editor" : "Active · Editor")
                          : (lang === "es" ? "Pendiente de registro" : "Pending registration")}
                      </span>
                    </div>
                    <div
                      className="text-xs text-[#8A8886] mt-1 truncate"
                      style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                    >
                      {c.email}
                    </div>
                    {viewerIsOwner && c.status === "pending" && (
                      <div className="mt-2.5 bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm px-3 py-2 text-xs text-[#5C5B5A] space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span>
                            {lang === "es"
                              ? "Ya hemos enviado el email. Si tu socio no lo recibe:"
                              : "We've already sent the invite email. If your partner doesn't see it:"}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <button
                            type="button"
                            onClick={() => resendInvite(c.email)}
                            disabled={resending === c.email}
                            className="inline-flex items-center gap-1 text-[#2563EB] hover:text-[#1D4ED8] font-medium disabled:opacity-50"
                            data-testid={`resend-invite-${c.email}`}
                          >
                            {resending === c.email ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                            ) : (
                              <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                            )}
                            {lang === "es" ? "Reenviar email" : "Resend email"}
                          </button>
                          <span className="text-[#8A8886]">·</span>
                          <button
                            type="button"
                            onClick={() => copyInviteLink(c.email)}
                            className="inline-flex items-center gap-1 text-[#2563EB] hover:text-[#1D4ED8] font-medium"
                            data-testid={`copy-invite-link-${c.email}`}
                          >
                            {copiedEmail === c.email ? (
                              <>
                                <Check className="h-3.5 w-3.5" strokeWidth={2} />
                                {lang === "es" ? "Copiado" : "Copied"}
                              </>
                            ) : (
                              <>
                                <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                                {lang === "es" ? "Copiar link" : "Copy link"}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {viewerIsOwner && (
                    <button
                      type="button"
                      onClick={() => revoke(c.email)}
                      disabled={revoking === c.email}
                      className="text-[#8A8886] hover:text-[#9E2A2B] transition-colors mt-0.5 shrink-0"
                      aria-label="revoke"
                      data-testid={`revoke-collab-${c.email}`}
                    >
                      {revoking === c.email ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      )}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 pt-5 border-t border-[#E8E6E1] flex justify-end">
          <SecondaryButton onClick={onClose} testId="collaborators-done-btn">
            {lang === "es" ? "Listo" : "Done"}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
